// Shared number-formatting helpers for the simulations feature.
// Locale is hard-coded to en-US to match the existing Summary/Cards convention.

const LOCALE = "en-US";

export const formatWithCommas = (value, digits = 2) => {
  const n = Number(value);
  const opts = {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  };
  if (!Number.isFinite(n)) return (0).toLocaleString(LOCALE, opts);
  return n.toLocaleString(LOCALE, opts);
};

export const formatSignedCurrency = (value, digits = 2) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const abs = formatWithCommas(Math.abs(safe), digits);
  return safe < 0 ? `-$${abs}` : `$${abs}`;
};

/* ── Card rounding policy ──────────────────────────────────────────────────
 *
 * Whole numbers when the figure is big enough that decimals are noise, decimals
 * when it is small enough that dropping them would distort it.
 *
 * ── Why the cut-off is 10 and not 1 ───────────────────────────────────────
 * Rounding to a whole number moves a value by up to 0.5. What that is worth
 * depends entirely on the magnitude:
 *
 *     a value in [1, 10)   can be wrong by up to  33%   (1.49 -> 1)
 *     a value at or above 10       is wrong by at most   5%   (10.5 -> 10)
 *
 * So 10 is where whole-number rounding stops being a rendering choice and
 * starts being a misstatement. Measured over husky-2040-prod and husky-2040 at
 * day, month and year granularity, per-device and summary
 * (12,566 power / 12,566 energy / 6,853 money figures):
 *
 *             below 1      in [1, 10)     at or above 10
 *     kW      2.04%          9.19%           88.77%
 *     kWh     0.85%          1.80%           97.35%
 *     $       0.15%         11.72%           88.14%
 *
 * The [1, 10) band is not a rounding error nobody meets: it is about one figure
 * in nine for kW and for dollars. All three units show the same structure -- the
 * same 33% worst case below 10, the same sub-5% worst case above it -- so they
 * share one threshold. A per-unit threshold would buy nothing the data asks for
 * and would let per-device and summary cards disagree.
 *
 * At summary magnitudes nothing is affected: the smallest monthly figure across
 * both scenarios is $80.07 and the smallest yearly one is $5,240.91, so a
 * month or year view is entirely whole numbers, which is what was asked for.
 * The decimals surface on daily views and small devices, which is exactly where
 * dropping them would have hurt.
 *
 * ── The threshold reads the underlying value, never the displayed one ──────
 * $0.996 is below the threshold, so it renders "$1.00". Testing the displayed
 * value instead would be circular -- you would have to round to decide how to
 * round -- and 0.996 would sit exactly on the boundary, flipping between "$1"
 * and "$1.00" depending on which pass you believed. One comparison against the
 * input is deterministic and cannot oscillate.
 *
 * ── A real value must never render as a zero ──────────────────────────────
 * Two decimals still round 0.002 to "0.00", and the smallest measured figures
 * are 0.0020 kW and 0.0020 kWh. Those render as "<0.01", not as zero. A
 * measured zero is exactly 0 and renders "$0" / "0 kW", and it stays
 * distinguishable from the em-dash (nothing recorded) and the blank cell
 * (no such quantity) because nothing else can collapse into it. */

/** Below this magnitude a card keeps decimals; at or above it, whole numbers. */
export const CARD_WHOLE_NUMBER_THRESHOLD = 10;

/** Decimal places for a card figure. Reads the underlying value, not the
 *  rendered one, so the choice cannot oscillate on a boundary. */
export const cardDigitsFor = (value) => {
  const n = Math.abs(Number(value));
  if (!Number.isFinite(n) || n === 0) return 0;
  return n >= CARD_WHOLE_NUMBER_THRESHOLD ? 0 : 2;
};

/* Below this, even two decimals render as "0.00". The smallest figures in the
 * current data (0.0020 kW, 0.0020 kWh) are here, so this is load-bearing rather
 * than defensive. */
export const CARD_MIN_RENDERABLE = 0.005;

/** True for a non-zero value too small to show at the card's precision. */
export const isBelowCardPrecision = (value) => {
  const n = Math.abs(Number(value));
  return Number.isFinite(n) && n > 0 && n < CARD_MIN_RENDERABLE;
};
